import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of, switchMap, timer, withLatestFrom } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import {
    defaultAllowManualSelection,
    defaultAppMode,
    defaultInputMode,
    defaultMasterDataCacheDurationInMs,
    defaultScanFlowRegistrationSubmitIntervalMs,
    defaultUserSessionExpirationTime,
} from 'app/features/settings/models';
import { SettingsStorageService } from 'app/features/settings/services';
import {
    updateAppSettings,
    updateSettingsSuccess,
} from 'app/features/settings/state/settings/settings.actions';
import {
    selectAppModeIfDefined,
    selectAppSettingsIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { getApplicationName, isPersonalScanMode } from 'app/features/settings/utils';
import { MasterData, UserSession } from 'app/shared/models';
import { DeviceInfoStorageService, MasterDataStorageService, TerminalClient } from 'app/shared/services';
import { isValidApiConfig, secondsToMilliseconds } from 'app/shared/utils';
import {
    preloadApiConfigSuccess,
    registerApiConfigSuccess,
    resetApiConfig,
} from 'app/state/api-config/api-config.actions';
import {
    selectApiConfigState,
    selectIsApiConfigValidIfLoaded,
} from 'app/state/api-config/api-config.selectors';
import { selectMasterData, selectMasterDataStatus } from 'app/state/master-data/master-data.selectors';
import { loginSuccess, supervisorLogout } from 'app/state/user-session/user-session.actions';
import { selectLatestSupervisorInHistoryIfDefined } from 'app/state/user-session/user-session.selectors';

import {
    initializingMasterDataStore,
    loadMasterDataCancelled,
    loadMasterDataFailure,
    loadMasterDataFromServer,
    loadMasterDataSuccess,
    preloadMasterDataFinished,
    refreshMasterData,
    resetMasterDataState,
    startPeriodicMasterDataUpdates,
} from './master-data.actions';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class MasterDataEffects {
    preloadMasterDataOnInitialization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(initializingMasterDataStore),
            map(() => this.masterDataStorageService.getMasterDataFromStore()),
            filter((cachedMasterData): cachedMasterData is MasterData => !!cachedMasterData),
            map((cachedMasterData) => preloadMasterDataFinished({ masterData: cachedMasterData })),
        );
    });

    loadMasterDataFromServer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadMasterDataFromServer),
            withLatestFrom(this.store.select(selectApiConfigState), this.store.pipe(selectAppModeIfDefined)),
            switchMap(([, apiConfig, appMode]) => {
                if (!isValidApiConfig(apiConfig)) return of(loadMasterDataCancelled());
                const deviceId = this.deviceInfoStorageService.getOrAddDeviceId();
                const applicationName = getApplicationName(appMode);
                return this.terminalClient.getMasterData(apiConfig, deviceId, applicationName).pipe(
                    map((masterData) => loadMasterDataSuccess({ masterData })),
                    catchError((error) => of(loadMasterDataFailure({ error: error?.toString() }))),
                );
            }),
        );
    });

    putToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadMasterDataSuccess),
                map(({ masterData }) => {
                    this.masterDataStorageService.putMasterDataToStorage(masterData);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    updateSettingsFromMasterData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMasterDataSuccess),
            switchMap(({ masterData }) => {
                const settingsFromStorage = this.settingsService.getAppSettingsFromStorage();

                const isPersonalScan = isPersonalScanMode(
                    masterData.deviceProfile?.appMode,
                    masterData.deviceProfile?.inputMode,
                );
                const allowManualSelectionState = isPersonalScan
                    ? (settingsFromStorage.allowManualSelection ?? defaultAllowManualSelection)
                    : defaultAllowManualSelection;
                // TODO: Update allowManualSelection when device profile is implemented
                return masterData.deviceProfile?.name
                    ? [
                          updateAppSettings({
                              settings: {
                                  allowManualSelection: allowManualSelectionState,
                                  appMode: masterData.deviceProfile?.appMode ?? defaultAppMode,
                                  inputModes: [masterData.deviceProfile?.inputMode ?? defaultInputMode],
                                  userSessionExpirationTime:
                                      settingsFromStorage.userSessionExpirationTime ??
                                      defaultUserSessionExpirationTime,
                                  masterDataCacheDurationInMs: defaultMasterDataCacheDurationInMs,
                                  scanFlowRegistrationSubmitIntervalMs:
                                      secondsToMilliseconds(
                                          masterData.deviceProfile?.submissionIntervalSeconds,
                                      ) ?? defaultScanFlowRegistrationSubmitIntervalMs,
                              },
                          }),
                      ]
                    : EMPTY;
            }),
        ),
    );

    updateUserSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMasterDataSuccess),
            withLatestFrom(this.store.select(selectLatestSupervisorInHistoryIfDefined)),
            switchMap(([{ masterData }, supervisor]) => {
                if (!masterData.deviceProfile?.name) return EMPTY;

                const settingsFromStorage = this.settingsService.getAppSettingsFromStorage();
                const newAppMode = masterData.deviceProfile?.appMode;
                const newInputMode = masterData.deviceProfile?.inputMode;

                if (newAppMode !== settingsFromStorage.appMode) {
                    if (newAppMode === 'personal') {
                        return [supervisorLogout(), updateSettingsSuccess()];
                    }
                    if (newAppMode === 'multi-user' && supervisor) {
                        const supervisorSession: UserSession = {
                            credentials: supervisor.credentials,
                            data: supervisor.data,
                            startedOn: Date.now(),
                            userInfo: supervisor.userInfo,
                        };
                        return [loginSuccess({ userSession: supervisorSession }), updateSettingsSuccess()];
                    }
                }

                if (newInputMode && !settingsFromStorage.inputModes?.includes(newInputMode))
                    return [updateSettingsSuccess()];
                return EMPTY;
            }),
        ),
    );

    triggerStartPeriodicMasterDataUpdates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMasterDataSuccess),
            map(() => startPeriodicMasterDataUpdates()),
        ),
    );

    startPeriodicMasterDataUpdates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPeriodicMasterDataUpdates),
            switchMap(() => timer(0, 10000)),
            map(() => refreshMasterData()),
        ),
    );

    refreshMasterData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(refreshMasterData),
            withLatestFrom(
                this.store.pipe(selectIsApiConfigValidIfLoaded),
                this.store.pipe(selectAppSettingsIfDefined),
                this.store.select(selectMasterDataStatus),
                this.store.select(selectMasterData),
            ),
            map(([, isApiConfigValid, settings, status, masterData]) => ({
                isApiConfigValid,
                settings,
                status,
                masterData,
            })),
            filter(({ isApiConfigValid }) => isApiConfigValid),
            filter(({ status }) => status !== 'refreshing' && status !== 'loading'),
            filter(({ settings, masterData }) => {
                if (masterData) {
                    const now = Date.now();
                    const passedCachedTime = now - masterData.date;
                    if (passedCachedTime < settings.masterDataCacheDurationInMs) {
                        return false;
                    }
                }
                return true;
            }),
            map(() => loadMasterDataFromServer()),
        ),
    );
    removeMasterDataFromStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(resetMasterDataState),
                map(() => {
                    this.masterDataStorageService.clearMasterData();
                    return true;
                }),
            ),
        { dispatch: false },
    );

    loadMasterDataFromServerAfterPreloadApiConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(preloadApiConfigSuccess),
            map(() => loadMasterDataFromServer()),
        ),
    );

    resetMasterDataAfterResetApiConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetApiConfig),
            map(() => resetMasterDataState()),
        ),
    );

    loadMasterDataAfterRegisterApiConfigSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(registerApiConfigSuccess),
            map(() => loadMasterDataFromServer()),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private terminalClient: TerminalClient,
        private masterDataStorageService: MasterDataStorageService,
        private deviceInfoStorageService: DeviceInfoStorageService,
        private settingsService: SettingsStorageService,
    ) {}
}
